/**
 * DateRangeSelector Component
 *
 * Provides a dropdown for selecting the dashboard date range filter.
 * Supports 7-day, 30-day, 90-day, and all-time ranges.
 *
 * @module pages/DashboardPage/components/DateRangeSelector
 */

import { memo, useMemo } from 'react';
import { Select } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type {
  DateRange,
  DateRangeSelectorProps,
} from './DateRangeSelector.types';
import styles from './DateRangeSelector.module.css';

const DATE_RANGE_OPTIONS: readonly DateRange[] = [
  '7d',
  '30d',
  '90d',
  'all',
] as const;

export const DateRangeSelector = memo(function DateRangeSelector({
  value,
  onChange,
}: DateRangeSelectorProps) {
  const { t } = useTranslation();

  const options = useMemo(
    () =>
      DATE_RANGE_OPTIONS.map((range) => ({
        value: range,
        label: t(`dashboard.dateRange.${range}`),
      })),
    [t]
  );

  const handleChange = (selected: string | null): void => {
    if (selected) {
      onChange(selected as DateRange);
    }
  };

  return (
    <Select
      size="sm"
      data={options}
      value={value}
      onChange={handleChange}
      allowDeselect={false}
      aria-label={t('dashboard.dateRangeLabel')}
      className={styles.selector}
    />
  );
});

DateRangeSelector.displayName = 'DateRangeSelector';
